import React from 'react';
import { connect } from 'react-redux';
import Promise from 'bluebird';
import { AdobePass, PlayerEvents, PlayerError, PlayerPlacement } from 'enums';
import { contentSelect, contentUnSelect } from 'common/actions/appActions';
import { createAndPerformStartAuthorizationAction, createAsyncGetMetadataAction } from 'common/actions/authenticationActions';
import { nielsenTrackingSelector } from 'selectors/analyticsSelectors';
import { playerTechSelector } from 'selectors/appSelector';
import initialEmbedSelector from 'selectors/initialEmbedSelector';
import onDeckContentSelector from 'selectors/onDeckContentSelector';
import pageSelector from 'selectors/pageSelector';
import playingContentSelector from 'selectors/playingContentSelector';
import providerSelector from 'selectors/providerSelector';
import { userAuthSelector } from 'selectors/authenticationSelectors';
import { contentHasAuthType, shallowCheckAuthentication } from 'lib/auth';
import { isEmpty } from 'lib/util';
import injectIntl, { intlShape } from 'common/injectIntl';
import { getPlayerOptions, getAdobeDCRConfig } from './util';
import {
    createContinuousPlayAction,
    createAutoEmbedTimerCompleteAction,
    createAutoplayUpdatedAction,
    createAuthorizationErrorAction,
    createContentPlayingAction,
    createPlayerReadyAction,
    createProgramChangeAction,
    createPlayerEmbeddingAction,
    createPlayerEmbeddedAction
} from './actions';

const { METADATA } = AdobePass;

// Begin Higher Order Component definition
function connectPlayer(WrappedComponent) {
    class ESPNPlayer extends React.Component {
        constructor(props) {
            super(props);
            this.state = {
                autoEmbedCountDown: this.props.initialAutoEmbedCountDown,
                deferPlayerInitialization: false, // if we have nothing to play yet
                initialized: false,
                initializedWithAuthZError: false
            };
            this.onToggleAutoPlay = this.onToggleAutoPlay.bind(this);
            this.onContentLoaded = this.onContentLoaded.bind(this);
            this.onProgramChange = this.onProgramChange.bind(this);
            this.playerReady = this.playerReady.bind(this);
            this.initializePlayer = this.initializePlayer.bind(this);
            this.handleClick = this.handleClick.bind(this);
            this.unregisterEvents = this.unregisterEvents.bind(this);
            this.registerEvents = this.registerEvents.bind(this);
            this.startAutoEmbedTimer = this.startAutoEmbedTimer.bind(this);
            this.unembedPlayer = this.unembedPlayer.bind(this);
            this.embedPlayer = this.embedPlayer.bind(this);
            // reference to the BrightCove player
            this.player = null;
            // is the component mounted?
            this._isMounted = false;
            // unmounting if we detect a route chage
            this._isUnMounting = false;
            // hold a reference to the timer to clear later
            this._autoEmbedTimer = null;
            this._isAuthorizing = false;
        }

        componentDidMount() {
            this._isMounted = true;
            this.startAutoEmbedTimer();
            this.initializePlayer();
        }

        componentWillReceiveProps(nextProps) {
            if (this._isMounted === false || this._isUnMounting === true) {
                return;
            }

            // if the user is no longer authd for the playing content, unembed the player
            if (shallowCheckAuthentication(this.props.playingContent, this.props.userAuth, false) &&
                !shallowCheckAuthentication(nextProps.playingContent, nextProps.userAuth, false)) {
                this.unembedPlayer();
                return;
            }
            // player initialization is deferred if there's no selected content, and page api has not yet returned
            if (this.state.deferPlayerInitialization === true && nextProps.scoringComplete === true &&
                (nextProps.initialEmbedContent && !isEmpty(nextProps.initialEmbedContent))) {
                this.setState({ deferPlayerInitialization: false });
                this.initializePlayer(nextProps);
            }

            // if the route has changed, anticipate the component unmounting
            if (this.props.routing.locationBeforeTransitions.pathname !==
                nextProps.routing.locationBeforeTransitions.pathname) {
                this._isUnMounting = true;
                this.unembedPlayer();
                return;
            }

            const onDeckContent = nextProps.onDeckContent;
            if (isEmpty(onDeckContent) || (this.props.onDeckContent.id === onDeckContent.id) ||
                nextProps.player.embedding === true || this._isAuthorizing === true) {
                return;
            }

            if (onDeckContent.sourceId === 'SHORTSTOPLIVE') {
                this.embedPlayer(nextProps, onDeckContent, { token: 'xbox_watch', tokenType: 'DEVICE' });
            } else if (onDeckContent.type === 'video') {
                this.embedPlayer(nextProps, onDeckContent);
            } else if (contentHasAuthType(onDeckContent, 'isp') && nextProps.userAuth.indexOf('isp') !== -1) {
                this.embedPlayer(nextProps, onDeckContent, { token: 'online', tokenType: 'DEVICE' });
            } else if (contentHasAuthType(onDeckContent, 'mvpd')) {
                const adobePassInfo = onDeckContent.adobePassInfo;
                if (adobePassInfo) {
                    const { messages, intl: { formatMessage } } = nextProps;
                    this._isAuthorizing = true;
                    this.props.dispatch(createAndPerformStartAuthorizationAction(onDeckContent.adobeRSS))
                        .then((authorization) => {
                            if (authorization.resourceId && authorization.token) {
                                return this.props.dispatch(createAsyncGetMetadataAction(METADATA.UPSTREAM_USERID))
                                    .then(accountId => {
                                        // if accountId doesn't exist, use a random number to avoid stream limiting everyone
                                        this.embedPlayer(nextProps, onDeckContent, authorization, {
                                            mvpd: adobePassInfo.abbreviation,
                                            accountId
                                        });
                                    });
                            }
                            return Promise.reject(new Error(formatMessage(messages['player.error.unexpectedMessage'])));
                        })
                        .catch(({ message }) => {
                            const { dispatch, player, playerPlacement } = this.props;

                            // dont initially embed player on hero with an error
                            if (
                                !this.state.initializedWithAuthZError &&
                                playerPlacement !== PlayerPlacement.PLAYBACK
                            ) {
                                // if the user clicks on the event, don't do this logic again
                                this.setState({
                                    initializedWithAuthZError: true
                                });
                                dispatch(contentUnSelect());
                                return;
                            }

                            dispatch(createAuthorizationErrorAction(message));
                            dispatch(createContentPlayingAction({ content: onDeckContent, error: true }));

                            const errorMessage = (message && message !== 'null') ?
                                message : formatMessage(messages['player.error.defaultMessage']);
                            const errorObj = {
                                type: PlayerError.AUTHORIZATION,
                                message: errorMessage
                            };

                            // on first embed, embed with an error
                            if (player.embedded === false) {
                                this.embedPlayer(nextProps, onDeckContent, {}, {}, errorObj);
                            } else {
                                this.player.bundle.error(errorObj);
                            }
                        }).finally(() => {
                            this._isAuthorizing = false;
                        });
                }
            }
        }

        shouldComponentUpdate() {
            return (this._isMounted === true && this._isUnMounting === false);
        }

        componentWillUnmount() {
            this._isMounted = false;
            clearTimeout(this._autoEmbedTimer);
            this.unregisterEvents();
            this.unembedPlayer();
        }

        onToggleAutoPlay(event, { autoplay = true }) {
            this.props.dispatch(createAutoplayUpdatedAction(autoplay));
        }

        onContentLoaded(event, mediaData = {}) {
            if (event.type === PlayerEvents.ESPN_CONTENT_LOADED) {
                if (mediaData.clipId) {
                    this.props.dispatch(createContinuousPlayAction(mediaData.clipId));
                } else {
                    // we can use espn.contentloaded to get the actual content that loaded on intial playback despite what
                    // was passed in on the URL
                    this.props.dispatch(createProgramChangeAction(mediaData.id));
                }
                // update nielsen tracking info with the new content
                this.player.analytics.configure({
                    adobeDCR: getAdobeDCRConfig(this.props.nielsenTrackingData, {}, this.props.env)
                });
            }
        }

        onProgramChange(event, { id, callback }) {
            if (event.type === PlayerEvents.ESPN_PROGRAM_CHANGE) {
                // guard against program change called with the same id
                const { playingContent = {} } = this.props;
                if (playingContent.id === id) {
                    return;
                }
                this.props.dispatch(createProgramChangeAction(id)).then(() => {
                    // update nielsen tracking info with the new
                    this.player.analytics.configure({
                        adobeDCR: getAdobeDCRConfig(
                            this.props.nielsenTrackingData,
                            { startType: 'live-show-boundary' },
                            this.props.env
                        )
                    });
                    // this actually triggers the analytics call for program changes
                    if (callback && typeof callback === 'function') {
                        callback();
                    }
                });
            }
        }

        // fire actions and set state to indicate the player is ready for embed
        playerReady(initialEmbedContent = null) {
            const { disableAuthPrompt, dispatch, placement, playerPlacement, userInitiated } = this.props;
            const playerReadyAction = dispatch(
                createPlayerReadyAction(playerPlacement, initialEmbedContent)
            );

            if (playerReadyAction.autoEmbed === true) {
                dispatch(contentSelect({
                    ...initialEmbedContent,
                    disableAuthPrompt,
                    initialEmbed: true,
                    placement,
                    userInitiated
                }));
            }
            this.setState({ initialized: true });
        }

        // called on componentDidMount (if autoplay), handleClick, or componentWillReceiveProps for deferred initialization.
        // takes props as a parameter for when called from componentWillReceiveProps, which passes "nextProps" in
        initializePlayer(nextProps) {
            const props = nextProps || this.props;
            if (this.state.initialized === false) {
                if (this._isMounted === false) {
                    return;
                }

                const {
                    initialEmbedContent,
                    scoringComplete,
                    playerTech
                } = props;

                // if there is no content yet (no selected event and page request hasn't completed),
                // defer player initialization to componentWillReceiveProps
                if ((!initialEmbedContent || isEmpty(initialEmbedContent)) || scoringComplete === false) {
                    this.setState({ deferPlayerInitialization: true });
                    return;
                }

                const playerTechConfig = {
                    optimizeLive: true,
                    native: (playerTech === 'native')
                };

                const EVP = require('espn-web-player-bundle');
                this.player = new EVP(playerTech, playerTechConfig);

                this.player.ready(() => {
                    this.playerReady(initialEmbedContent);
                });
            }
        }

        handleClick(event) {
            event.preventDefault();
            if (this.props.clickHandler) {
                this.props.clickHandler(event);
            }

            // only initialize player if it hasn't been already
            if (this.props.player.embedded === false) {
                this.initializePlayer(this.props);
            }
        }

        unregisterEvents() {
            const { player } = this.player;
            if (player) {
                player.off(PlayerEvents.ESPN_AUTOPLAY_TOGGLED, this.onToggleAutoPlay);
                player.off(PlayerEvents.ESPN_PROGRAM_CHANGE, this.onProgramChange);
                player.off(PlayerEvents.ESPN_CONTENT_LOADED, this.onContentLoaded);
            }
        }

        registerEvents() {
            const { player } = this.player;
            if (player) {
                player.on(PlayerEvents.ESPN_AUTOPLAY_TOGGLED, this.onToggleAutoPlay);
                player.on(PlayerEvents.ESPN_PROGRAM_CHANGE, this.onProgramChange);
                player.on(PlayerEvents.ESPN_CONTENT_LOADED, this.onContentLoaded);
            }
        }

        startAutoEmbedTimer() {
            if (this.state.autoEmbedCountDown <= 0) {
                this.props.dispatch(createAutoEmbedTimerCompleteAction());
                clearTimeout(this._autoEmbedTimer);
                return;
            }
            if (this._isMounted === true && this.state.autoEmbedCountDown > 0) {
                this.setState({ autoEmbedCountDown: this.state.autoEmbedCountDown - 1 });
                this._autoEmbedTimer = setTimeout(this.startAutoEmbedTimer, 1000);
            }
        }

        unembedPlayer() {
            if (this.player && this.player.player) {
                this.player.unembed();
            }
            this.props.dispatch(createPlayerEmbeddedAction(false));
        }

        embedPlayer(props = this.props, content, authorization = {}, streamLimiting = {}, error = null, callback = () => {}) {
            // unembed any previously playing content
            this.unembedPlayer();

            // get the id of the DOM element from ref
            const wrappedComponent = this.refs && this.refs.wrappedComponent;
            const playerElement = wrappedComponent && wrappedComponent.refs && wrappedComponent.refs.playerElement;
            const targetElementId = playerElement && playerElement.id;

            if (targetElementId) {
                const {
                    env,
                    locale,
                    nielsenTrackingData,
                    dispatch,
                    isMobile,
                    provider
                } = props;
                const opts = getPlayerOptions(
                    content,
                    authorization,
                    streamLimiting,
                    nielsenTrackingData,
                    isMobile,
                    error,
                    env,
                    locale,
                    provider
                );

                dispatch(createPlayerEmbeddingAction(true));

                this.player.embed(null, targetElementId, opts, (player) => {
                    dispatch(createPlayerEmbeddingAction(false));
                    if (this._isMounted === false || this._isUnMounting === true) {
                        player.unembed();
                        return;
                    }

                    this.unregisterEvents();
                    this.registerEvents();
                    dispatch(createPlayerEmbeddedAction(true));
                    dispatch(createContentPlayingAction({ content, error: !!error }));
                    callback();
                });
            } else {
                console.error(`Cannot embed player.  ${WrappedComponent.displayName} is missing "playerElement" ref`);  // eslint-disable-line
            }
        }

        render() {
            return (
                <WrappedComponent ref="wrappedComponent" {...this.props} {...this.state} clickHandler={this.handleClick} />
            );
        }
    }

    ESPNPlayer.propTypes = {
        clickHandler: React.PropTypes.func,
        disableAuthPrompt: React.PropTypes.bool,            // ability to turn off auth prompt when selecting content
        dispatch: React.PropTypes.func,
        env: React.PropTypes.string,
        locale: React.PropTypes.string,
        initialAutoEmbedCountDown: React.PropTypes.number,  // time until autoplay
        initialEmbedContent: React.PropTypes.object,        // from selector, prefer selectedContent, fallback to hero,
        intl: intlShape.isRequired,
        isMobile: React.PropTypes.bool,                     // eslint-disable-line react/no-unused-prop-types
        messages: React.PropTypes.object.isRequired,
        nielsenTrackingData: React.PropTypes.object,        // from selector
        onDeckContent: React.PropTypes.object,              // from selector, the content to be played next ()
        playerPlacement: React.PropTypes.string,            // placement of the actual player
        placement: React.PropTypes.string,                  // original placement of selected content for nielsen tracking
        player: React.PropTypes.object,                     // from selector, player state
        playerTech: React.PropTypes.string,
        playingContent: React.PropTypes.object,             // from selector, currently playing content
        provider: React.PropTypes.object,                   // from selector, provider to pass to Conviva
        routing: React.PropTypes.object,                    // mapped from react-redux-router
        scoringComplete: React.PropTypes.bool,              // from selector, indicates scoring status
        userAuth: React.PropTypes.array,                    // from selector
        userInitiated: React.PropTypes.bool                 // from router location "state", used for analytics
    };

    ESPNPlayer.defaultProps = {
        disableAuthPrompt: true,
        initialAutoEmbedCountDown: 2,
        initialEmbedContent: {},
        userAuth: []
    };

    const mapStateToProps = (state) => {
        const {
            app: { isMobile = false, env, locale },
            player,
            routing
        } = state;

        return {
            env,
            locale,
            initialEmbedContent: initialEmbedSelector(state),
            isMobile,
            nielsenTrackingData: nielsenTrackingSelector(state),
            onDeckContent: onDeckContentSelector(state),
            player,
            playerTech: playerTechSelector(state),
            playingContent: playingContentSelector(state),
            provider: providerSelector(state),
            routing,
            scoringComplete: pageSelector(state).scored,
            userAuth: userAuthSelector(state)
        };
    };
    return connect(mapStateToProps)(injectIntl(ESPNPlayer));
}

export default connectPlayer;



// WEBPACK FOOTER //
// ./app/player/connectPlayer.js